/**
 * In-memory cache for the account password.
 *
 * The password is captured once at login and held purely in RAM so that it
 * can be used transiently to:
 *  1. Regenerate an expired validation token (`getLastValidationToken`).
 *  2. Auto-decrypt the locally stored recovery key (`getSecretStorageKey`).
 *
 * It is **never** written to localStorage, sessionStorage, IndexedDB, or any
 * other persistent medium. It is cleared as soon as it has served its purpose
 * (after the recovery-key attempt) or when the device-listener confirms the
 * device state on first recheck.
 */

import { decryptText, encryptText } from "../utils";

/** Keyed by userId/email. */
const _cache = new Map<string, string>();

export class DthLoginPasswordCache {
    /** Store the plaintext password for the given user, in memory only. */
    public static async set(userId: string, password: string): Promise<void> {
        if (!userId || !password) return;
        const encryptedPassword = await encryptText(password, userId);
        _cache.set(userId, encryptedPassword);
    }

    /** Return the cached password, or `null` if not present. */
    public static async get(userId: string): Promise<string | null> {
        if (!userId) return null;
        const decryptedPassword = _cache.get(userId);
        if (!decryptedPassword) return null;
        return await decryptText(decryptedPassword, userId);
    }

    /** Remove the cached password for a specific user. */
    public static clearUser(userId: string): void {
        _cache.delete(userId);
    }

    /** Remove all cached passwords (e.g. on logout). */
    public static clear(): void {
        _cache.clear();
    }
}
